import { useState, useEffect } from 'react'
import { useAbm } from '../hooks/useAbm'
import AbmTable from '../components/AbmTable'
import Modal from '../components/Modal'
import Boton from '../components/Boton'
import api from '../api/client'

// Los roles NO se hardcodean: se leen de /roles (SISTEMA → Roles los crea y
// renombra). La lista fija de acá quedó desactualizada cuando "ventas" pasó a
// llamarse "ventas call" y aparecieron "ventas sucursal" y "marketing": el
// selector ofrecía un rol inexistente y escondía los reales (ago 2026).
// `super_administrador` no es un rol spatie —es un flag sobre administrador—
// así que se agrega a mano.
const SUPER_ADMIN = { value: 'super_administrador', label: 'Super Administrador' }

/** "ventas call" → "Ventas call". Sin diccionario: el nombre lo pone el ABM. */
const etiqueta = (nombre) => String(nombre).charAt(0).toUpperCase() + String(nombre).slice(1)

const EMPTY = { nombre: '', email: '', password: '', rol: '', roles_adicionales: [], lista_precios: '', codigo_vendedor: '', caja_presea: '', telegram_chat_id: '', vendedores_reservados_visibles: [], activo: true }

export default function UsuariosPage() {
  // Dar de baja a un usuario lo DESACTIVA (queda referenciado por NVs,
  // historiales y salidas). Por defecto se ven solo los activos: con los
  // inactivos mezclados, después de limpiar los viejos parecía que volvían.
  const [verInactivos, setVerInactivos] = useState(false)
  const { rows, loading, create, update, remove, refresh: recargar } = useAbm('usuarios', {
    params: verInactivos ? { inactivos: 1 } : null,
  })
  const [modal, setModal] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [listas, setListas] = useState([])
  const [roles, setRoles] = useState([])
  const [vinculo, setVinculo] = useState(null)
  const [genVinculo, setGenVinculo] = useState(false)
  const [reset2fa, setReset2fa] = useState(null) // 'enviando' | mensaje de resultado

  useEffect(() => {
    api.get('/listas-precios')
      .then(({ data }) => setListas(Array.isArray(data) ? data : (data.data || [])))
      .catch(() => setListas([]))
    api.get('/roles')
      .then(({ data }) => setRoles((data.data || []).map(r => ({ value: r.name, label: etiqueta(r.name) }))))
      .catch(() => setRoles([]))
  }, [])

  // Selector del rol principal: los de la base + super administrador.
  const ROLES = [SUPER_ADMIN, ...roles]
  const ROL_LABEL = Object.fromEntries(ROLES.map(r => [r.value, r.label]))
  // Roles que pueden SUMARSE al principal (los admin no: ya ven todo).
  const ROLES_ADICIONALES = roles.filter(r => r.value !== 'administrador')
  // Códigos de vendedor reservados (config del backend, viene en cada usuario).
  const reservados = rows?.[0]?.vendedores_reservados || []

  const openCreate = () => { setForm(EMPTY); setErr(''); setVinculo(null); setReset2fa(null); setModal('new') }
  const openEdit = (row) => {
    setForm({ nombre: row.nombre, email: row.email, password: '', rol: row.rol, roles_adicionales: row.roles_adicionales || [], lista_precios: row.lista_precios || '', codigo_vendedor: row.codigo_vendedor || '', caja_presea: row.caja_presea ?? '', telegram_chat_id: row.telegram_chat_id || '', vendedores_reservados_visibles: row.vendedores_reservados_visibles || [], activo: row.activo })
    setErr(''); setVinculo(null); setReset2fa(null); setModal(row)
  }

  const generarVinculo = async () => {
    setGenVinculo(true)
    try {
      const { data } = await api.post(`/usuarios/${modal.id}/vincular-telegram`)
      setVinculo(data)
    } catch {
      setVinculo({ error: 'No se pudo generar el código.' })
    } finally { setGenVinculo(false) }
  }

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true); setErr('')
    try {
      const payload = { ...form }
      if (!payload.password) delete payload.password
      if (payload.caja_presea === '') payload.caja_presea = null
      // Guardar no pisa el vínculo de Telegram (incidente 21 oct: se generó el
      // código con la ficha abierta, la persona se vinculó y el Guardar mandó
      // el campo vacío que había quedado en pantalla, borrando su chat). El
      // chat viaja solo si alguien lo cambió a mano en este formulario.
      if (modal !== 'new' && (payload.telegram_chat_id || '') === (modal.telegram_chat_id || '')) delete payload.telegram_chat_id
      if (modal === 'new') await create(payload)
      else await update(modal.id, payload)
      setModal(null)
    } catch (error) {
      const msgs = error.response?.data?.errors
      setErr(msgs ? Object.values(msgs).flat().join(' ') : (error.response?.data?.message || 'Error al guardar'))
    } finally { setSaving(false) }
  }

  const set = (key) => e => setForm(f => ({ ...f, [key]: e.target.value }))

  const columns = [
    { key: 'nombre', label: 'Usuario' },
    { key: 'clave', label: 'Clave', render: () => <span style={{ letterSpacing: 2, color: '#94a3b8' }}>••••••</span> },
    { key: 'email', label: 'Mail' },
    { key: 'lista_precios', label: 'Lista de precio', render: v => v || <span style={{ color: '#94a3b8' }}>— según cliente —</span> },
    { key: 'codigo_vendedor', label: 'Cód. vendedor', render: v => v || <span style={{ color: '#94a3b8' }}>—</span> },
    { key: 'caja_presea_efectiva', label: 'Caja', render: v => v || <span style={{ color: '#94a3b8' }}>—</span> },
    { key: 'rol', label: 'Rol', render: (v, row) => (
      <>
        {ROL_LABEL[v] || v}
        {(row.roles_adicionales || []).length > 0 && (
          <span style={{ marginLeft: 6, fontSize: '0.72rem', color: '#64748b' }}>
            + {(row.roles_adicionales || []).map(r => ROL_LABEL[r] || r).join(', ')}
          </span>
        )}
      </>
    ) },
    { key: 'activo', label: 'Activo', render: v => v ? 'Sí' : 'No' },
    // Segundo factor: si lo tiene activado. Un admin sin 2FA no puede operar
    // hasta activarlo (obligatorio para su rol).
    { key: 'tfa_habilitado', label: '2FA', render: (v, row) => (
      v ? 'Sí' : <span style={{ color: row.tfa_obligatorio ? '#92400e' : '#94a3b8' }}>{row.tfa_obligatorio ? 'No (pendiente)' : 'No'}</span>
    ) },
  ]

  // Reseteo del segundo factor de otro usuario (perdió el celular y los
  // códigos de rescate). Le cierra las sesiones: al entrar lo activa de nuevo.
  const resetear2fa = async () => {
    if (!window.confirm(`Resetear el segundo factor de ${modal.nombre}?\n\nSe borran su clave, sus códigos de rescate y sus dispositivos confiables, y se le cierran las sesiones abiertas. Al volver a entrar tiene que activarlo de nuevo.`)) return
    setReset2fa('enviando')
    try {
      const { data } = await api.post(`/usuarios/${modal.id}/totp/reset`)
      setReset2fa(data.message || 'Segundo factor reseteado.')
      setModal(m => ({ ...m, tfa_habilitado: false }))
      await recargar()
    } catch (error) {
      setReset2fa(error.response?.data?.message || 'No se pudo resetear el segundo factor.')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        {verInactivos && (
          <span style={{ fontSize: 13, color: '#64748b' }}>
            Usuarios dados de baja — para reactivar uno, editalo y marcá “Activo”.
          </span>
        )}
        <Boton variante={verInactivos ? 'secondary' : 'ghost'} sm onClick={() => setVerInactivos(v => !v)}>
          {verInactivos ? '← Ver activos' : 'Ver dados de baja'}
        </Boton>
      </div>
      <AbmTable
        title={verInactivos ? 'Usuarios dados de baja' : 'Usuarios'}
        columns={columns}
        rows={rows}
        loading={loading}
        onCreate={verInactivos ? undefined : openCreate}
        onEdit={openEdit}
        onDelete={verInactivos ? undefined : remove}
      />
      {modal && (
        <Modal title={modal === 'new' ? 'Nuevo Usuario' : 'Editar Usuario'} onClose={() => setModal(null)}>
          <form onSubmit={handleSave} style={styles.form}>
            <div>
              <label style={styles.label}>Usuario (nombre) *</label>
              <input style={styles.input} value={form.nombre} onChange={set('nombre')} required />
            </div>
            <div>
              <label style={styles.label}>Mail *</label>
              <input style={styles.input} type="email" value={form.email} onChange={set('email')} required />
            </div>
            <div>
              <label style={styles.label}>{modal === 'new' ? 'Clave *' : 'Nueva clave (dejar en blanco para no cambiar)'}</label>
              <input style={styles.input} type="password" value={form.password} onChange={set('password')} {...(modal === 'new' ? { required: true, minLength: 8 } : {})} />
            </div>
            <div>
              <label style={styles.label}>Rol *</label>
              <select style={styles.input} value={form.rol} onChange={set('rol')}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {/* Un usuario puede tener más de un rol: los adicionales SUMAN
                permisos (menú y bot ven la unión). Ocultos si el principal es
                admin/super-admin, que ya ven todo. */}
            {!['administrador', 'super_administrador'].includes(form.rol) && (
              <div>
                <label style={styles.label}>Roles adicionales</label>
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '4px 0' }}>
                  {ROLES_ADICIONALES.filter(r => r.value !== form.rol).map(r => (
                    <label key={r.value} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={(form.roles_adicionales || []).includes(r.value)}
                        onChange={e => setForm(f => ({
                          ...f,
                          roles_adicionales: e.target.checked
                            ? [...(f.roles_adicionales || []), r.value]
                            : (f.roles_adicionales || []).filter(x => x !== r.value),
                        }))}
                      />
                      {r.label}
                    </label>
                  ))}
                </div>
                <p style={styles.hint}>El usuario ve el menú y usa el bot con la suma de todos sus roles.</p>
              </div>
            )}
            <div>
              <label style={styles.label}>Lista de precio</label>
              <select style={styles.input} value={form.lista_precios} onChange={set('lista_precios')}>
                <option value="">— Según el cliente (el vendedor elige en la NV) —</option>
                {listas.map(l => {
                  const cod = l.codigo_presea || l.codigo
                  return <option key={l.id} value={cod}>{l.nombre}{cod ? ` (${cod})` : ''}</option>
                })}
              </select>
              <p style={styles.hint}>Si asignás una lista, todas las NV de este usuario la usan. Si la dejás en blanco, el vendedor elige la lista al crear la NV.</p>
            </div>
            <div>
              <label style={styles.label}>Código vendedor (Presea)</label>
              <input style={styles.input} value={form.codigo_vendedor} onChange={set('codigo_vendedor')} placeholder="Ej: 12" />
              <p style={styles.hint}>Código del vendedor en Presea. Viaja en la NV (FAC 22). Se carga a mano hasta que lo traiga el import de vendedores.</p>
            </div>
            {reservados.length > 0 && (
              <div>
                <label style={styles.label}>Ve operaciones de vendedores reservados</label>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  {reservados.map(cod => (
                    <label key={cod} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={(form.vendedores_reservados_visibles || []).includes(cod)}
                        onChange={e => setForm(f => ({
                          ...f,
                          vendedores_reservados_visibles: e.target.checked
                            ? [...(f.vendedores_reservados_visibles || []), cod]
                            : (f.vendedores_reservados_visibles || []).filter(x => x !== cod),
                        }))}
                      />
                      Vendedor {cod}
                    </label>
                  ))}
                </div>
                <p style={styles.hint}>Las operaciones de estos vendedores solo las ven los administradores. Tildá los que esta persona también puede ver (NV, remitos y notas de envío).</p>
              </div>
            )}
            <div>
              <label style={styles.label}>Caja financiera (Presea)</label>
              <input style={styles.input} type="number" value={form.caja_presea} onChange={set('caja_presea')} placeholder="250 operadores · 100 administradores" />
              <p style={styles.hint}>Cada recibo en efectivo va a la caja de quien lo cobra. Vacío = default por rol: 250 (todos) / 100 (administradores).</p>
            </div>
            <div>
              <label style={styles.label}>Telegram chat ID (bot de pedidos)</label>
              <input style={styles.input} value={form.telegram_chat_id} onChange={set('telegram_chat_id')} placeholder="Ej: 6200087531" />
              <p style={styles.hint}>El vendedor le manda /start a @LigierLGR_bot y el bot le contesta su chat ID. Cargalo acá y ya puede pasar pedidos por Telegram.</p>
              {modal !== 'new' && (
                <div style={styles.vinculoBox}>
                  <Boton variante="secondary" sm type="button" onClick={generarVinculo} disabled={genVinculo}>
                    {genVinculo ? 'Generando…' : '🔗 Generar código de vínculo'}
                  </Boton>
                  <span style={styles.hint}>Más fácil que copiar el chat ID a mano: el vendedor manda el código al bot y se vincula solo.</span>
                  {vinculo?.codigo && (
                    <div style={styles.vinculoOk}>
                      Código: <strong style={{ fontFamily: 'monospace', fontSize: '1.05rem' }}>{vinculo.codigo}</strong><br />
                      El vendedor le escribe a <strong>@{vinculo.bot}</strong> y manda: <code>/vincular {vinculo.codigo}</code>
                      <br /><span style={{ color: '#64748b' }}>Vence en 10 minutos. Al vincularse, su chat ID se carga solo.</span>
                    </div>
                  )}
                  {vinculo?.error && <p style={styles.error}>{vinculo.error}</p>}
                </div>
              )}
            </div>
            <label style={styles.checkRow}>
              <input type="checkbox" checked={form.activo} onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))} />
              Activo
            </label>
            {modal !== 'new' && (
              <div>
                <label style={styles.label}>Segundo factor (2FA)</label>
                <div style={styles.vinculoBox}>
                  <span style={{ fontSize: '0.85rem' }}>
                    {modal.tfa_habilitado
                      ? <>Activado{modal.tfa_activado_at ? ` el ${new Date(modal.tfa_activado_at).toLocaleDateString('es-AR')}` : ''}.</>
                      : <>No activado{modal.tfa_obligatorio ? ' (su rol lo requiere: no puede operar hasta activarlo)' : ''}.</>}
                  </span>
                  {modal.tfa_habilitado && (
                    <div>
                      <Boton variante="destructive" sm type="button" onClick={resetear2fa} disabled={reset2fa === 'enviando'}>
                        {reset2fa === 'enviando' ? 'Reseteando…' : 'Resetear 2FA'}
                      </Boton>
                    </div>
                  )}
                  <span style={styles.hint}>Para cuando perdió el celular y los códigos de rescate. Le cierra las sesiones y al entrar lo activa de nuevo.</span>
                  {reset2fa && reset2fa !== 'enviando' && <div style={styles.vinculoOk}>{reset2fa}</div>}
                </div>
              </div>
            )}
            {err && <p style={styles.error}>{err}</p>}
            <div style={styles.actions}>
              <Boton variante="ghost" type="button" onClick={() => setModal(null)}>Cancelar</Boton>
              <Boton variante="primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Boton>
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

const styles = {
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.875rem', color: '#374151', marginBottom: '0.25rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.9375rem', boxSizing: 'border-box' },
  hint: { fontSize: '0.78rem', color: '#64748b', margin: '0.3rem 0 0' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' },
  vinculoBox: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem', padding: '0.6rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' },
  vinculoOk: { fontSize: '0.85rem', color: '#0f172a', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px', padding: '0.5rem 0.6rem', lineHeight: 1.5 },
  error: { color: '#dc2626', fontSize: '0.875rem', margin: 0 },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.5rem' },
}
